import React from 'react';
import { Slider } from '@ark-ui/react/slider';
import clsx from 'clsx';
import type { BaseInputProps } from '../../types/inputs';
import styles from './inputs.module.scss';

export interface RangeSliderInputProps extends BaseInputProps<number[]> {
  min?: number;
  max?: number;
  step?: number;
  /** Minimální počet kroků (mezera) mezi oběma thumby. */
  minStepsBetweenThumbs?: number;
  /** Zobrazit aktuální rozsah vedle popisku. */
  showValue?: boolean;
  /** Přístupné názvy jednotlivých thumbů (dolní, horní mez). */
  thumbLabels?: [string, string];
  className?: string;
}

/**
 * Atomický posuvník s rozsahem (dva thumby) postavený na Ark UI `Slider`.
 *
 * RHF drží `number[]` jako dvojici `[min, max]`; Ark pracuje s polem hodnot,
 * takže `value` předáváme přímo (bez mapování na jeden prvek jako single
 * {@link SliderInput}). Oba thumby pojmenovává `ids.label` (popisek
 * `<FormField />`) — zag totiž na thumb vždy přidá `aria-labelledby` a bez
 * platné reference by vznikl visící odkaz. Dolní/horní mez se rozlišuje přes
 * `aria-valuetext` (`getAriaValueText`), protože `aria-labelledby` by per-thumb
 * `aria-label` přebilo. Výplň `Slider.Range` se mezi thumby roztáhne automaticky.
 */
export const RangeSliderInput: React.FC<RangeSliderInputProps> = ({
  name,
  id,
  value,
  onChange,
  onBlur,
  ref,
  disabled,
  readOnly,
  hasError,
  describedBy,
  labelId,
  testAttributes,
  min = 0,
  max = 100,
  step = 1,
  minStepsBetweenThumbs = 0,
  showValue = true,
  thumbLabels = ['Dolní mez', 'Horní mez'],
  className,
}) => {
  // Ochrana proti undefined/neúplné hodnotě — vždy dvojice v rozsahu.
  const safeValue =
    Array.isArray(value) && value.length === 2 ? value : [min, max];

  return (
    <Slider.Root
      name={name}
      value={safeValue}
      min={min}
      max={max}
      step={step}
      minStepsBetweenThumbs={minStepsBetweenThumbs}
      disabled={disabled}
      readOnly={readOnly}
      invalid={hasError}
      getAriaValueText={({ value: v, index }) =>
        `${thumbLabels[index] ?? ''} ${v}`.trim()
      }
      ids={{
        label: labelId,
        hiddenInput: (index) => (index === 0 ? id : `${id}-${index}`),
      }}
      onValueChange={(details) => onChange(details.value)}
      onBlur={onBlur}
      className={clsx(styles.sliderRoot, className)}
      {...testAttributes}
    >
      {showValue && <Slider.ValueText className={styles.sliderValueText} />}
      <Slider.Control className={styles.sliderControl}>
        <Slider.Track className={styles.sliderTrack}>
          <Slider.Range className={styles.sliderRange} />
        </Slider.Track>
        <Slider.Thumb
          index={0}
          ref={ref}
          className={styles.sliderThumb}
          aria-describedby={describedBy}
        >
          <Slider.HiddenInput />
        </Slider.Thumb>
        <Slider.Thumb index={1} className={styles.sliderThumb}>
          <Slider.HiddenInput />
        </Slider.Thumb>
      </Slider.Control>
    </Slider.Root>
  );
};

RangeSliderInput.displayName = 'RangeSliderInput';
